import { FC } from 'react';
import { ActionButtons } from '@components/molecules/ActionButtons';
import { PendingApproval } from '@models/task.model';

interface ApprovalItemProps {
  approval: PendingApproval;
  isLoading?: boolean;
  onView: (approval: PendingApproval) => void;
  onApprove: (approval: PendingApproval) => void;
  onReject: (approval: PendingApproval) => void;
}

const formatTime = (iso: string): string => {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) {
    const diffMins = Math.max(0, Math.floor(diffMs / (1000 * 60)));
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

/**
 * Sprint 12, story-055 (U11/N2). One row in the approvals dropdown, mirroring `BudgetItem`'s
 * shape and reusing `ActionButtons` (`showApprovalActions`) unmodified — the same green
 * Approve / red Reject pair already proven live on the Schedule page.
 */
export const ApprovalItem: FC<ApprovalItemProps> = ({
  approval,
  isLoading = false,
  onView,
  onApprove,
  onReject,
}) => {
  const isClientApproval = approval.kind === 'CLIENT_APPROVAL';

  return (
    <div
      data-testid={`approvals-item-${approval.taskId}`}
      className={`flex flex-col gap-2 p-3 bg-white border border-blue-200 rounded-md mb-2 ${
        isLoading ? 'opacity-50 pointer-events-none' : ''
      }`}>
      <div
        className="cursor-pointer"
        data-testid={`approvals-item-view-${approval.taskId}`}
        onClick={(e) => {
          e.stopPropagation();
          onView(approval);
        }}>
        <div className="font-semibold text-gray-900 text-sm truncate">{approval.taskName}</div>
        {approval.projectName && (
          <div className="text-xs text-gray-600 truncate">{approval.projectName}</div>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${
              isClientApproval
                ? 'bg-purple-50 text-purple-700 border-purple-200'
                : 'bg-blue-50 text-blue-700 border-blue-200'
            }`}>
            {isClientApproval ? 'CLIENT APPROVAL' : 'INTERNAL APPROVAL'}
          </span>
          <span className="text-xs text-gray-500">
            {isClientApproval ? 'delegated work waiting on your approval' : 'waiting on your approval'}
          </span>
        </div>
        {approval.approveOutcome === 'CLIENT_APPROVAL' && (
          <div
            className="text-xs text-gray-500 mt-1"
            data-testid={`approvals-item-sends-to-client-${approval.taskId}`}>
            Approving sends it to your client for their approval
          </div>
        )}
        {approval.requestedByName && (
          <div className="text-xs text-gray-400 mt-1">
            Requested by {approval.requestedByName} · {formatTime(approval.enteredApprovalAt)}
          </div>
        )}
      </div>
      <ActionButtons
        showApprovalActions
        showEditAction={false}
        loading={isLoading}
        onApprove={() => onApprove(approval)}
        onReject={() => onReject(approval)}
        approveTestId={`approvals-item-approve-${approval.taskId}`}
        rejectTestId={`approvals-item-reject-${approval.taskId}`}
      />
    </div>
  );
};

export default ApprovalItem;
